import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { contractConfigApi } from '../services/api';
import type { BankGuidance as BankGuidanceData, BankGuidanceBank, BankLimitStatus } from '../types';

/** The labels in a given language, as in Admin's preview; else the interface language. */
function useGuideTranslation(language?: 'sv' | 'en') {
  const { t, i18n } = useTranslation();
  const fixed = language && typeof i18n.getFixedT === 'function' ? i18n.getFixedT(language) : null;
  const locale = (language ?? i18n.language)?.startsWith('en') ? 'en-GB' : 'sv-SE';
  return { t: (fixed ?? t) as typeof t, locale };
}

const STATUS_BADGE: Record<BankLimitStatus, { className: string; icon: string }> = {
  within: { className: 'bg-success', icon: 'check' },
  exceeds: { className: 'bg-warning text-dark', icon: 'alert-triangle' },
  unknown: { className: 'bg-secondary', icon: 'help-circle' }
};

/**
 * A bank's guide text: paragraphs, and lines starting with "- " as a list.
 * Rendered as text, never as HTML.
 */
export function GuidanceText({ text }: { text: string }) {
  const blocks: Array<{ list: boolean; lines: string[] }> = [];
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const list = line.startsWith('- ');
    const content = list ? line.slice(2).trim() : line;
    const last = blocks[blocks.length - 1];
    if (list && last?.list) last.lines.push(content);
    else blocks.push({ list, lines: [content] });
  }
  return (
    <>
      {blocks.map((block, index) =>
        block.list ? (
          <ul className="bank-guidance-list" key={index}>
            {block.lines.map((line, lineIndex) => <li key={lineIndex}>{line}</li>)}
          </ul>
        ) : (
          <p className="mb-2" key={index}>{block.lines[0]}</p>
        )
      )}
    </>
  );
}

export function BankLimitBadge({ bank, language }: { bank: BankGuidanceBank; language?: 'sv' | 'en' }) {
  const { t } = useGuideTranslation(language);
  const badge = STATUS_BADGE[bank.limitStatus];
  return (
    <span className={`badge bank-guidance-badge ${badge.className}`} data-testid={`bank-guidance-status-${bank.id}`}>
      <i className={`feather-${badge.icon}`} aria-hidden="true" />
      {t(`bankGuidance.status.${bank.limitStatus}`)}
    </span>
  );
}

/** One bank with its limits, status, how to raise the limit and how to reach it. */
export function BankGuidanceCard({ bank, language }: { bank: BankGuidanceBank; language?: 'sv' | 'en' }) {
  const { t, locale } = useGuideTranslation(language);
  const money = (amount: number) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency: 'SEK', maximumFractionDigits: 0 }).format(amount);
  const limit = (value: number | null, exceeded: boolean | null) =>
    value === null ? (
      <dd className="bank-guidance-limit-missing">{t('bankGuidance.limits.notSet')}</dd>
    ) : (
      <dd className={exceeded ? 'bank-guidance-limit-exceeded' : undefined}>{money(value)}</dd>
    );
  const headingId = useId();

  return (
    <article className="bank-guidance-bank" aria-labelledby={headingId} data-testid={`bank-guidance-bank-${bank.id}`}
      lang={bank.fallbackLanguage ? 'sv' : undefined}>
      <h3 className="bank-guidance-bank-name" id={headingId}>{bank.name}</h3>
      <BankLimitBadge bank={bank} language={language} />
      <dl className="bank-guidance-limits">
        <div>
          <dt>{t('bankGuidance.limits.perTransaction')}</dt>
          {limit(bank.maxPerTransaction, bank.amountExceedsTransactionLimit)}
        </div>
        <div>
          <dt>{t('bankGuidance.limits.perDay')}</dt>
          {limit(bank.dailyLimit, bank.amountExceedsDailyLimit)}
        </div>
      </dl>
      <div className="bank-guidance-raise">
        <strong className="d-block mb-1">
          {bank.raiseLimitSelfPath ? t('bankGuidance.raiseSelf') : t('bankGuidance.canRaise')}
        </strong>
        {bank.raiseLimitSelfPath && (
          <p className="mb-2">
            {bank.raiseLimitSelfPath.split('\n').map((line, index) => (
              <React.Fragment key={index}>{index > 0 && <br />}{line}</React.Fragment>
            ))}
          </p>
        )}
        {bank.guidanceText.trim()
          ? <GuidanceText text={bank.guidanceText} />
          : <p className="mb-2">{t('bankGuidance.noGuide')}</p>}
      </div>
      {(bank.infoUrl || bank.supportPhone) && (
        <div className="bank-guidance-contacts">
          {bank.infoUrl && (
            <a className="btn btn-outline-primary bank-guidance-contact" href={bank.infoUrl} target="_blank"
              rel="noopener noreferrer" data-testid={`bank-guidance-link-${bank.id}`}>
              {t('bankGuidance.infoLink', { bank: bank.name })}
              <i className="feather-external-link" aria-hidden="true" />
              <span className="visually-hidden">{t('bankGuidance.newTab')}</span>
            </a>
          )}
          {bank.supportPhone && (
            <a className="btn btn-outline-primary bank-guidance-contact" href={`tel:${bank.supportPhone.replace(/[^\d+]/g, '')}`}
              aria-label={t('bankGuidance.call', { bank: bank.name, phone: bank.supportPhone })}>
              <i className="feather-phone" aria-hidden="true" />
              {bank.supportPhone}
            </a>
          )}
        </div>
      )}
    </article>
  );
}

/**
 * "Innan du sätter in pengar" in the deposit (ClickUp 869eu8fd9): the
 * Beställare's banks against the amount. Expanded the first time per deposit,
 * collapsed after that with the status still visible. Information only: it
 * never blocks the payment methods, and it shows nothing if it cannot load.
 */
export function BankGuidance({ projectId, contractId, amountOre }: { projectId: string; contractId: string; amountOre?: number }) {
  const { t, i18n } = useTranslation();
  const language = i18n.language?.startsWith('en') ? 'en' : 'sv';
  const [guidance, setGuidance] = useState<BankGuidanceData | null>(null);
  const [expanded, setExpanded] = useState(false);
  const recorded = useRef<string | null>(null);
  const bodyId = useId();
  const titleId = useId();

  useEffect(() => {
    let current = true;
    setGuidance(null);
    (amountOre === undefined ? contractConfigApi.getBankGuidance(projectId, contractId, language) : contractConfigApi.getBankGuidance(projectId, contractId, language, amountOre))
      .then(data => {
        if (!current) return;
        const valid = !!data && Array.isArray(data.banks);
        setGuidance(valid ? data : null);
        setExpanded(valid && data.expanded === true);
      })
      .catch(() => {
        if (current) setGuidance(null);
      });
    return () => { current = false; };
  }, [projectId, contractId, language, amountOre]);

  // Seen expanded once for this deposit: start collapsed next time.
  useEffect(() => {
    if (!guidance?.expanded || recorded.current === contractId) return;
    recorded.current = contractId;
    void contractConfigApi.markBankGuidanceSeen(projectId, contractId).catch(() => undefined);
  }, [guidance, projectId, contractId]);

  if (!guidance) return null;

  return (
    <section className="bank-guidance mb-4" aria-labelledby={titleId} data-testid="bank-guidance">
      <button
        type="button"
        className="bank-guidance-toggle"
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={() => setExpanded(value => !value)}
        data-testid="bank-guidance-toggle"
      >
        <i className="feather-shield bank-guidance-icon" aria-hidden="true" />
        <span className="bank-guidance-title" id={titleId}>
          {expanded ? t('bankGuidance.title') : t('bankGuidance.collapsedTitle')}
        </span>
        {!expanded && guidance.banks.length > 0 && (
          <span className="bank-guidance-summary" data-testid="bank-guidance-summary">
            {guidance.banks.map(bank => (
              <span key={bank.id} className={`badge bank-guidance-badge ${STATUS_BADGE[bank.limitStatus].className}`}>
                <i className={`feather-${STATUS_BADGE[bank.limitStatus].icon}`} aria-hidden="true" />
                {t(`bankGuidance.statusShort.${bank.limitStatus}`, { bank: bank.name })}
              </span>
            ))}
          </span>
        )}
        <span className="bank-guidance-toggle-label">
          {expanded ? t('bankGuidance.hide') : t('bankGuidance.show')}
          <i className={expanded ? 'feather-chevron-up' : 'feather-chevron-down'} aria-hidden="true" />
        </span>
      </button>

      <div id={bodyId} className="bank-guidance-body" hidden={!expanded} data-testid="bank-guidance-body">
        {guidance.fallbackLanguage && (
          <div className="alert alert-warning bank-guidance-fallback" role="note" data-testid="bank-guidance-fallback">
            <i className="feather-globe" aria-hidden="true" />
            {t('bankGuidance.fallback')}
          </div>
        )}
        <p>{t('bankGuidance.introLimits')}</p>
        <p>
          <strong className="bank-guidance-emphasis">{t('bankGuidance.introControlled')}</strong>{' '}
          {t('bankGuidance.introStopped')}
        </p>
        {guidance.noBankSelected ? (
          <div className="alert alert-info bank-guidance-no-bank" role="status" data-testid="bank-guidance-no-bank">
            <span>
              <i className="feather-info me-2" aria-hidden="true" />
              {t('bankGuidance.noBank')}
            </span>
            <Link to="/profile" className="btn btn-outline-primary bank-guidance-contact">
              {t('bankGuidance.goToProfile')}
            </Link>
          </div>
        ) : (
          <>
            <p>{t('bankGuidance.introBanks')}</p>
            <div className="row g-3">
              {guidance.banks.map(bank => (
                <div className="col-12 col-md-6" key={bank.id}>
                  <BankGuidanceCard bank={bank} />
                </div>
              ))}
            </div>
          </>
        )}
        <p className="bank-guidance-footnote">{t('bankGuidance.footnote')}</p>
      </div>
    </section>
  );
}
